import React, { useMemo, useState } from "react";
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { useDispatch } from "react-redux";
import AddDialogStyle from "styles/application/AddDialogStyle";
import { showError, showSuccess } from "../../../redux/common/actions";
import {
	generateReportNow,
	toApiReportType,
} from "services/reportSubscriptions";
import ScheduleReportForm from "./ScheduleReportForm";
import { useFilterSummary } from "./hooks/useFilterSummary";
import { useFilterValues } from "./hooks/useFilterValues";
import { useReportFilterContext } from "./hooks/useReportFilterContext";
import { useScheduleForm } from "./hooks/useScheduleForm";

const ADD = AddDialogStyle();

const useStyles = makeStyles()(() => ({
	content: {
		minWidth: "720px",
		paddingTop: "8px !important",
		"@media (max-width: 768px)": {
			minWidth: "0",
		},
	},
	formBusy: {
		opacity: 0.5,
		pointerEvents: "none",
		userSelect: "none",
	},
	emailNowRow: {
		display: "flex",
		justifyContent: "flex-end",
		marginTop: "8px",
	},
}));

// Reads the user's profile email from the same session/local storage cache the
// rest of the app already relies on. Returning a string keeps the lookup self
// contained — no new context plumbing required for this MVP.
const readProfileEmail = () => {
	try {
		const raw = sessionStorage.getItem("me") || localStorage.getItem("me");
		if (!raw) return "";
		return JSON.parse(raw)?.email || "";
	} catch {
		return "";
	}
};

const ScheduleReportModal = ({
	open,
	onClose,
	availableReports = [],
	initialValues,
	mode = "create",
	onSubmit,
	lockReportSelection = false,
}) => {
	const { classes } = useStyles();
	const dispatch = useDispatch();
	const fallbackEmail = useMemo(readProfileEmail, []);
	const liveFilters = useFilterSummary();
	// In edit mode the profile card has no live FilterContext mounted - fall back
	// to the snapshot captured when the subscription was created.
	const savedFilters = initialValues?._filterSummary;
	const currentFilters = liveFilters.length > 0 ? liveFilters : (savedFilters || []);
	const filterValues = useFilterValues();
	const getReportFilterContext = useReportFilterContext();
	// In the profile-card edit flow there's no live FilterProvider mounted,
	// so getReportFilterContext() yields {}. Fall back to the snapshot saved
	// against the subscription so "Email me a copy now" doesn't fire an
	// unfiltered report.
	const resolveFilterContext = () => {
		const live = getReportFilterContext();
		if (live && Object.keys(live).length > 0) return live;
		return initialValues?._filterContext ?? {};
	};

	const form = useScheduleForm({
		initialValues,
		fallbackEmail,
		availableReports,
		currentFilters,
	});

	const [submitting, setSubmitting] = useState(false);
	const handleSubmit = async () => {
		const payload = {
			...form.values,
			daysOfWeek: form.effectiveDays,
			deliveryEmail: form.values.deliveryEmail || fallbackEmail,
			// Snapshot of the page's current filter state in the backend
			// report-DTO shape (plural id arrays, fromDate/toDate, etc.).
			// Each filter component owns its translation via .reportParams.
			filterValues: resolveFilterContext(),
			// Human-readable [{label, value}] snapshot captured here so the
			// profile-card edit view can render selections without needing
			// a live FilterContext.
			filterSummary: currentFilters,
		};
		setSubmitting(true);
		try {
			if (onSubmit) {
				await onSubmit(payload);
			} else {
				dispatch(
					showSuccess(
						mode === "edit"
							? "Subscription updated."
							: "Report subscription created."
					)
				);
			}
			onClose?.();
		} finally {
			setSubmitting(false);
		}
	};

	const [emailingNow, setEmailingNow] = useState(false);
	// Distinct from the report-dropdown "Generate now" flow that opens
	// ReportGenerationDialog and auto-downloads. This ghost link inside the
	// schedule modal is for users that want the report emailed (e.g. to share
	// with someone, or to keep in their inbox) — same /api/reports/generate
	// endpoint, but sendEmail defaults to true server-side and there's no
	// polling dialog. We fire-and-forget here: success toast → backend emails
	// when the worker finishes.
	const handleEmailNow = async () => {
		if (!form.values.reportType) return;
		setEmailingNow(true);
		// Resolve once so the toast and the API call can't disagree: blank
		// field falls back to the profile email, same as the schedule submit.
		const recipientEmail = form.values.deliveryEmail || fallbackEmail || null;
		try {
			const res = await generateReportNow({
				reportType: toApiReportType(form.values.reportType),
				filterContext: resolveFilterContext(),
				name: form.values.name?.trim() || null,
				recipientEmail,
				subscriptionId: initialValues?._id || null,
			});
			if (!res?.status) {
				dispatch(
					showError(res?.data?.error || "Could not start the report")
				);
				return;
			}
			dispatch(
				showSuccess(
					res?.data?.message ||
						(recipientEmail
							? `We'll email a download link to ${recipientEmail} when it's ready.`
							: "We'll email you a download link when it's ready.")
				)
			);
		} finally {
			setEmailingNow(false);
		}
	};

	const submitLabel = mode === "edit" ? "Save Changes" : "Subscribe";

	return (
		<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
			<ADD.ActionContainer>
				<DialogTitle>
					<ADD.HeaderText>Schedule an Email Report</ADD.HeaderText>
				</DialogTitle>
				<ADD.ButtonContainer>
					<ADD.CancelButton
						onClick={onClose}
						variant="contained"
						disabled={submitting}
					>
						Cancel
					</ADD.CancelButton>
					<ADD.ConfirmButton
						onClick={handleSubmit}
						variant="contained"
						disabled={!form.isValid || submitting}
					>
						{submitting ? (
							<CircularProgress size={20} sx={{ color: "#FFFFFF" }} />
						) : (
							submitLabel
						)}
					</ADD.ConfirmButton>
				</ADD.ButtonContainer>
			</ADD.ActionContainer>
			<DialogContent className={classes.content}>
				<div
					className={submitting || emailingNow ? classes.formBusy : undefined}
					aria-busy={submitting || emailingNow}
				>
					<ScheduleReportForm
						values={form.values}
						setField={form.setField}
						setFrequency={form.setFrequency}
						toggleDay={form.toggleDay}
						summary={form.summary}
						availableReports={availableReports}
						currentFilters={currentFilters}
						fallbackEmail={fallbackEmail}
						lockReportSelection={lockReportSelection}
					/>
					<div className={classes.emailNowRow}>
						<Button
							onClick={handleEmailNow}
							disabled={!form.values.reportType || emailingNow || submitting}
							sx={{
								opacity:
									!form.values.reportType || emailingNow || submitting
										? 0.5
										: 1,
								"&:hover": {
									cursor:
										!form.values.reportType || emailingNow || submitting
											? "not-allowed"
											: "pointer",
								},
							}}
						>
							{emailingNow ? "Sending…" : "Email me a copy now"}
						</Button>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ScheduleReportModal;
